// Browser Service Modal Types
// Reuses types from browserServiceSlice with additions for new features

export enum BROWSER_REL {
  service = "service",
  region = "region",
  type = "type",
  item = "item",
  child = "child",
  self = "self",
  aspect = "aspect",
  roles = "roles",
  next = "next",
}

export interface BrowserLink {
  _type: "link";
  rel: BROWSER_REL;
  href: string;
  label?: string;
}

export interface BrowserData {
  _type: "data";
  _cpln: {
    label: string;
    ref: string;
    href?: string;
    mountUri?: string;
    hasChildren?: boolean;
  };
}

export interface BrowserRes {
  data?: BrowserData[];
  links: BrowserLink[];
}

// Column types for the modal
export type ColumnType = "services" | "regions" | "types" | "items" | "children" | "nestedItems";

export interface ColumnState {
  selectedHref?: string;
  filter: string;
  focusedIndex: number;
}

export interface BreadcrumbItem {
  level: number;
  type: ColumnType;
  label: string;
  href: string;
}

// Modal state managed by useReducer
export interface BrowserModalState {
  columns: Record<ColumnType, ColumnState>;
  breadcrumbs: BreadcrumbItem[];
  selections: BrowserData[];
  activeColumn: ColumnType;
}

// Reducer actions
export type BrowserModalAction =
  | { type: "SELECT_ITEM"; column: ColumnType; href: string; label: string }
  | { type: "SET_FILTER"; column: ColumnType; filter: string }
  | { type: "SET_FOCUSED_INDEX"; column: ColumnType; index: number }
  | { type: "SET_ACTIVE_COLUMN"; column: ColumnType }
  | { type: "CLEAR_AFTER_COLUMN"; column: ColumnType }
  | { type: "ADD_SELECTION"; item: BrowserData }
  | { type: "REMOVE_SELECTION"; ref: string }
  | { type: "TOGGLE_SELECTION"; item: BrowserData }
  | { type: "SET_SINGLE_SELECTION"; item: BrowserData }
  | { type: "CLEAR_SELECTIONS" }
  | { type: "NAVIGATE_TO_BREADCRUMB"; index: number }
  | { type: "RESET" };

// Props for cloud provider specific options
export interface ForGCPAzureProps {
  isActive: boolean;
  cloudaccountLink: string;
}

// Main modal props
export interface BrowserServiceModalProps {
  title: string;
  open: boolean;
  selectionType: "none" | "multi" | "single";
  onClose: (selections: BrowserData[]) => void;
  cloudaccountLink?: string;
  forGcpResource?: ForGCPAzureProps;
  forAzureScope?: ForGCPAzureProps;
  restrictToBucket?: boolean;
  restrictToParent?: boolean;
  restrictToVPCEndpointService?: boolean;
  dataTypeRestrictions?: string[];
}

// Column component props
export interface BrowserColumnProps {
  type: ColumnType;
  title: string;
  items: BrowserLink[] | BrowserData[] | undefined;
  isLoading: boolean;
  isActive: boolean;
  selectedHref?: string;
  filter: string;
  focusedIndex: number;
  selectionType: "none" | "multi" | "single";
  selections: BrowserData[];
  isClickable?: boolean;
  canNavigateDeeper?: boolean;
  onItemClick: (item: BrowserLink | BrowserData) => void;
  onItemSelect?: (item: BrowserData) => void;
  onFilterChange: (filter: string) => void;
  onFocusChange: (index: number) => void;
  onColumnFocus: () => void;
}

// Item component props
export interface BrowserItemProps {
  item: BrowserLink;
  isActive: boolean;
  isClickable: boolean;
  isFocused: boolean;
  onClick: () => void;
}

export interface BrowserDataItemProps {
  item: BrowserData;
  isActive: boolean;
  isSelected: boolean;
  isFocused: boolean;
  selectionType: "none" | "multi" | "single";
  canBeSelectedAsParent: boolean;
  canNavigateDeeper: boolean;
  onClick: () => void;
  onSelect: () => void;
}

// Breadcrumb props
export interface BrowserBreadcrumbProps {
  items: BreadcrumbItem[];
  onNavigate: (index: number) => void;
}

// Footer props
export interface ModalFooterProps {
  selectionType: "none" | "multi" | "single";
  selectedCount: number;
  isConfirmLoading: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}
